// src/pages/your-path/TaskTodo.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import LeftMenu from "../../components/left-menu";
import InquiryPops from "../../components/InquiryPops";

import {
  Grid,
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";

const LEGACY_OPEN_DEAL_STATUSES = ["New", "Contacted", "Offered", "Invoiced"];

const LEAD_STAGE_LABELS = {
  S1_NEW: "S1 New",
  S2_RESPONSIVE: "S2 Nurture",
  S3_QUALIFIED: "S3 Qualified",
  S4_WON: "S4 Paid",
  SX_BAD: "SX Bad",
};

const OPPORTUNITY_STATUS_LABELS = {
  OPEN: "Open",
  WON: "Won",
  DROPPED_TO_NURTURE: "Dropped to Nurture",
  LOST_BAD: "Lost Bad",
  ARCHIVED: "Archived",
};

const formatShortDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatShortDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getLeadStageLabel = (stage) => {
  return LEAD_STAGE_LABELS[stage] || stage || "Legacy";
};

const getOpportunityStatusLabel = (status) => {
  return OPPORTUNITY_STATUS_LABELS[status] || status || "-";
};

const getStageChipColor = (stage) => {
  if (stage === "S3_QUALIFIED") return "success";
  if (stage === "S2_RESPONSIVE") return "warning";
  if (stage === "S4_WON") return "primary";
  if (stage === "SX_BAD") return "error";
  return "default";
};

const getEffectivePIC = (task) => {
  const inq = task?.inquiry || {};
  const deal = task?.deal || {};

  return (
    inq.currentOwnerSales ||
    inq.Sales ||
    deal.Sales ||
    task?.Sales ||
    "-"
  );
};

const getEffectiveCounter = (task) => {
  const deal = task?.deal || {};

  if (typeof deal.salesFollowUpCounter === "number") {
    return deal.salesFollowUpCounter;
  }

  if (typeof deal.counterFU === "number") {
    return deal.counterFU;
  }

  return 0;
};

const getEffectiveDealStatus = (task) => {
  const deal = task?.deal || {};

  return (
    deal.opportunityStatus ||
    deal.status ||
    task?.opportunityStatus ||
    "-"
  );
};

const getHandledCategoryFromSave = ({ payload, serverData, selectedTask }) => {
  const salesAction = payload?.salesAction || "";
  const newLeadStage =
    serverData?.newLeadStage ||
    serverData?.inquiry?.leadStage ||
    serverData?.leadStage ||
    "";

  const newOpportunityStatus =
    serverData?.newOpportunityStatus ||
    serverData?.deal?.opportunityStatus ||
    serverData?.opportunityStatus ||
    "";

  const legacyDealStatus =
    serverData?.deal?.status ||
    payload?.dealStatus ||
    selectedTask?.deal?.status ||
    "";

  // V2 sales popup actions
  if (salesAction === "FOLLOW_UP_AGAIN") return "open";
  if (salesAction === "DROP_TO_NURTURE") return "closed";
  if (salesAction === "DROP_BAD") return "closed";

  // V2 stage/status fallback
  if (newLeadStage === "S3_QUALIFIED" && newOpportunityStatus === "OPEN") {
    return "open";
  }

  if (
    ["S2_RESPONSIVE", "S4_WON", "SX_BAD"].includes(newLeadStage) ||
    ["WON", "DROPPED_TO_NURTURE", "LOST_BAD", "ARCHIVED"].includes(
      newOpportunityStatus
    )
  ) {
    return "closed";
  }

  // Legacy fallback
  if (LEGACY_OPEN_DEAL_STATUSES.includes(legacyDealStatus)) {
    return "open";
  }

  return "closed";
};

export default function TaskTodo() {
  const user = useSelector((state) => state?.user?.sales || "");

  const [newTasks, setNewTasks] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  // Cache notes per task sampai page refresh
  const [noteDrafts, setNoteDrafts] = useState({});

  const totalTasks = useMemo(() => {
    return newTasks.length + todayTasks.length;
  }, [newTasks.length, todayTasks.length]);

  const decorateTasks = useCallback((list = []) => {
    return list.map((t) => ({
      ...t,
      handled: false,
      handledCategory: null, // "open" | "closed" | null
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch tasks untuk sales ini
  // ---------------------------------------------------------------------------
  const fetchTasks = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setLoadError("");

      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tasktodo`,
        {
          params: { user },
        }
      );

      const data = resp.data || {};

      setNewTasks(decorateTasks(data.newTasks || []));
      setTodayTasks(decorateTasks(data.todayTasks || []));
    } catch (err) {
      console.error("Error fetching tasktodo:", err);
      setLoadError(
        err?.response?.data?.error ||
          err?.message ||
          "Gagal memuat daftar tugas."
      );
    } finally {
      setLoading(false);
    }
  }, [user, decorateTasks]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // ---------------------------------------------------------------------------
  // Draft cache notes per task
  // ---------------------------------------------------------------------------
  const handleDraftChange = useCallback((taskId, value) => {
    if (!taskId) return;

    setNoteDrafts((prev) => ({
      ...prev,
      [taskId]: value,
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Klik baris → buka InquiryPops
  // ---------------------------------------------------------------------------
  const handleRowClick = (task, section) => {
    if (!task?.inquiry) {
      console.warn("Task tanpa inquiry:", task);
      return;
    }

    const inq = task.inquiry || {};
    const deal = task.deal || null;

    setSelectedTask({ ...task, section });

    setSelectedInquiry({
      ...inq,

      taskId: task._id,
      taskTitle: task.title || "",
      taskDueAt: task.dueAt || null,

      Sales:
        inq.currentOwnerSales ||
        inq.Sales ||
        deal?.Sales ||
        task?.Sales ||
        user,

      currentOwnerSales:
        inq.currentOwnerSales ||
        deal?.Sales ||
        task?.Sales ||
        user,

      pipelineVersion:
        inq.pipelineVersion ||
        task.pipelineVersion ||
        "legacy",

      leadStage:
        inq.leadStage ||
        task.leadStage ||
        "",

      highestLeadStage: inq.highestLeadStage || "",

      currentOpportunityId:
        inq.currentOpportunityId ||
        deal?._id ||
        null,

      deal,
      activities: inq.activities || [],
    });

    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedTask(null);
    setSelectedInquiry(null);
  };

  // ---------------------------------------------------------------------------
  // Dipanggil dari InquiryPops setelah save sukses
  // Supports:
  // - legacy /addInqActivity
  // - v2 /salesOpportunityUpdateV2
  // ---------------------------------------------------------------------------
  const handleInquirySaved = (oldInquiry, payload, serverData) => {
    if (!selectedTask) return;

    const updatedInquiryFromServer = serverData?.inquiry || {};
    const updatedDealFromServer = serverData?.deal || {};
    const newActivity = serverData?.activity || null;
    const newTaskFromServer = serverData?.task || null;

    const handledCategory = getHandledCategoryFromSave({
      payload,
      serverData,
      selectedTask,
    });

    const updateList = (setter) => {
      setter((prev) =>
        prev.map((t) => {
          if (t._id !== selectedTask._id) return t;

          const prevActivities = t?.inquiry?.activities || [];
          const nextActivities = newActivity
            ? [newActivity, ...prevActivities]
            : prevActivities;

          const mergedInquiry = {
            ...(t.inquiry || {}),
            ...(updatedInquiryFromServer || {}),
            activities: nextActivities,
          };

          const mergedDeal = {
            ...(t.deal || {}),
            ...(updatedDealFromServer || {}),
          };

          return {
            ...t,
            handled: true,
            handledCategory,

            // If backend created next task, show the new dueAt/title in-session
            // while still marking this row as handled.
            title:
              newTaskFromServer?.title ||
              t.title ||
              "",
            dueAt:
              newTaskFromServer?.dueAt ||
              t.dueAt ||
              null,

            status:
              handledCategory === "closed"
                ? "done"
                : t.status || "pending",

            pipelineVersion:
              mergedInquiry.pipelineVersion ||
              t.pipelineVersion ||
              "legacy",

            leadStage:
              mergedInquiry.leadStage ||
              t.leadStage ||
              "",

            opportunityStatus:
              mergedDeal.opportunityStatus ||
              t.opportunityStatus ||
              "",

            inquiry: mergedInquiry,
            deal: mergedDeal,
          };
        })
      );
    };

    if (selectedTask.section === "new") {
      updateList(setNewTasks);
    } else if (selectedTask.section === "today") {
      updateList(setTodayTasks);
    }

    if (selectedTask?._id && typeof payload?.noteText === "string") {
      setNoteDrafts((prev) => ({
        ...prev,
        [selectedTask._id]: payload.noteText,
      }));
    }
  };

  // ---------------------------------------------------------------------------
  // Export CSV
  // ---------------------------------------------------------------------------
  const handleExportCsv = () => {
    const rows = [];

    const pushFrom = (tasks, sectionLabel) => {
      tasks.forEach((t) => {
        const inq = t.inquiry || {};
        const deal = t.deal || {};

        rows.push({
          Section: sectionLabel,
          Nama: inq.Nama || "",
          Phone: inq.Phone || "",
          PIC: getEffectivePIC(t),
          LeadStage: inq.leadStage || t.leadStage || "",
          OpportunityStatus:
            deal.opportunityStatus ||
            t.opportunityStatus ||
            "",
          DealStatus: deal.status || "",
          FUCounter: getEffectiveCounter(t),
          Notes: t.title || "",
          DueDate: t.dueAt ? formatShortDate(t.dueAt) : "",
          TaskStatus: t.status || "",
          AdCode:
            inq.qualificationAdCode ||
            inq.latestAdCode ||
            inq.firstAdCode ||
            inq.AdCode ||
            "",
          ProdukInterest: inq.ProdukInterest || "",
          LokasiInterest: inq.LokasiInterest || "",
        });
      });
    };

    pushFrom(newTasks, "New");
    pushFrom(todayTasks, "Today");

    if (rows.length === 0) {
      window.alert("Tidak ada data untuk diexport.");
      return;
    }

    const header = Object.keys(rows[0]);

    const csvLines = [
      header.join(","),
      ...rows.map((r) =>
        header
          .map((key) => {
            const cell = (r[key] ?? "").toString().replace(/"/g, '""');
            return `"${cell}"`;
          })
          .join(",")
      ),
    ];

    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", `tasktodo_${user || "sales"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------------------------------------------------------------------------
  // Row color sesudah di-save pada session ini
  // ---------------------------------------------------------------------------
  const getRowBackgroundColor = (task) => {
    if (!task?.handled) return "inherit";
    if (task?.handledCategory === "closed") return "#eeeeee";
    if (task?.handledCategory === "open") return "#e8f5e9";
    return "inherit";
  };

  const renderStageChip = (task) => {
    const inq = task.inquiry || {};
    const stage = inq.leadStage || task.leadStage || "";

    if (!stage) {
      return <Chip size="small" label="Legacy" />;
    }

    return (
      <Chip
        size="small"
        label={getLeadStageLabel(stage)}
        color={getStageChipColor(stage)}
      />
    );
  };

  const renderOpportunityChip = (task) => {
    const status = getEffectiveDealStatus(task);

    if (!status || status === "-") {
      return <Typography variant="caption">-</Typography>;
    }

    return (
      <Typography variant="caption" color="text.secondary">
        {getOpportunityStatusLabel(status)}
      </Typography>
    );
  };

  // ---------------------------------------------------------------------------
  // Render table section
  // ---------------------------------------------------------------------------
  const renderSection = (title, color, tasks, sectionKey) => (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "inline-block",
          px: 2,
          py: 0.5,
          borderRadius: 1.5,
          backgroundColor: color,
          color: "#fff",
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {title} ({tasks.length})
      </Box>

      <Paper variant="outlined">
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nama</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nomor Telpon</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>PIC</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>FU</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: "center" }}
                    >
                      Tidak ada data.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task) => {
                  const inq = task.inquiry || {};

                  return (
                    <TableRow
                      key={task._id}
                      hover
                      onClick={() => handleRowClick(task, sectionKey)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: getRowBackgroundColor(task),
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {inq.Nama || "-"}
                        </Typography>

                        {!!inq.ProdukInterest && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              maxWidth: 160,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {inq.ProdukInterest}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>{inq.Phone || "-"}</TableCell>

                      <TableCell>{getEffectivePIC(task)}</TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          {renderStageChip(task)}
                          {renderOpportunityChip(task)}
                        </Stack>
                      </TableCell>

                      <TableCell>{getEffectiveCounter(task)}</TableCell>

                      <TableCell>
                        {task.dueAt ? formatShortDateTime(task.dueAt) : "-"}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 260,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {task.title || "-"}
                        </Typography>

                        {task.handled && (
                          <Typography
                            variant="caption"
                            color={
                              task.handledCategory === "closed"
                                ? "text.secondary"
                                : "success.main"
                            }
                            sx={{ display: "block" }}
                          >
                            {task.handledCategory === "closed"
                              ? "Handled - closed/drop"
                              : "Handled - next follow up saved"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );

  const selectedTaskDraft =
    selectedTask?._id && noteDrafts[selectedTask._id] !== undefined
      ? noteDrafts[selectedTask._id]
      : "";

  // ---------------------------------------------------------------------------
  // Render utama
  // ---------------------------------------------------------------------------
  return (
    <Grid container>
      {/* Left menu */}
      <Grid item xl={0.5} lg={0.7} md={0.9} sm={3.2}>
        <LeftMenu />
      </Grid>

      {/* Main content */}
      <Grid item xl={10.8} lg={10.6} md={10.4} sm={8.8} xs={12}>
        <Box sx={{ mt: 15 }} />

        <Box sx={{ pb: 4, px: 2 }}>
          {/* Bar atas */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h6">
                Tugas untuk: {user || "-"}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Total due task: {totalTasks}
              </Typography>

              {loading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Mengambil data...
                  </Typography>
                </Box>
              )}

              {!!loadError && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {loadError}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={fetchTasks} disabled={loading}>
                Refresh
              </Button>

              <Button
                variant="outlined"
                onClick={handleExportCsv}
                disabled={newTasks.length === 0 && todayTasks.length === 0}
              >
                Export ke Excel
              </Button>
            </Stack>
          </Box>

          {renderSection("New Qualified", "#e53935", newTasks, "new")}
          {renderSection("Today Follow Up", "#2e7d32", todayTasks, "today")}
        </Box>

        {/* Popup Inquiry / Deal */}
        {popupOpen && selectedInquiry && (
          <InquiryPops
            open={popupOpen}
            handleClose={handleClosePopup}
            data={selectedInquiry}
            actorId={user}
            onSave={handleInquirySaved}
            draftNote={selectedTaskDraft}
            onDraftChange={handleDraftChange}
            forceV2SalesFlow={true}
          />
        )}
      </Grid>
    </Grid>
  );
}